<template>
  <u-popover
    v-model:visible="popoverVisible"
    :placement="placement"
    :dropdown-class="dropdownClass"
    :offset="offset"
    :disabled="disabled"
    trigger="click"
  >
    <template #content>
      <div class="child-menu">
        <div class="avatar-setting">
          <div class="flex-shrink-0">
            <img :src="avatar" width="40" height="40" alt="avatar" />
          </div>
          <div class="user flex flex-col">
            <div class="flex items-center">
              <OneLineText
                :text="nickName"
                class-name="max-w-[204px] w-0 flex-1 nickName font-bold"
              />
              <BotIcon
                v-if="userExpireTime && shouldShowExpireTime"
                name="error"
                :size="16"
                :tip="
                  $t(
                    'views.layout.components.MenuList.618eca127e9e08d35e4b6d4fe9b833e4',
                    [userExpireTime]
                  )
                "
                :padding="[0, 0, 0, 4]"
                class="flex-shrink-0"
              />
            </div>

            <OneLineText :text="userName" class-name="w-[180px] user-name" />
          </div>
        </div>
        <div
          v-if="canViewCreditQuota"
          class="credit-quota-entry"
          data-testid="open-credit-quota-button"
          @click="handleCreditQuota"
        >
          <div class="flex items-center gap-1 min-w-0">
            <span class="credit-icon">
              <img
                src="@/assetsCommon/layout/star.svg"
                alt="credit"
                width="16"
                height="16"
              />
            </span>
            <span class="credit-title">{{
              $t(
                'views.layout.components.Avatar.041ab94da063eba28f49a1b8597722ef'
              )
            }}</span>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <span class="credit-value">{{ formatNumber(creditBalance) }}</span>
            <BotIcon name="left-arrow" :size="14" :padding="0" :rotate="180" />
          </div>
        </div>
        <div
          class="child-menu-item hover-bg acount"
          data-testid="open-account-setting-button"
          @click="handleAccount"
        >
          {{
            $t(
              'views.layout.components.MenuList.41a3bf0dfa2b3e08a2251e00378a7ec3'
            )
          }}
        </div>
        <div
          v-if="isCompanyUser"
          class="child-menu-item hover-bg acount"
          data-testid="open-enterprise-manage-button"
          @click="handleManage"
        >
          {{
            $t(
              'views.layout.components.Avatar.065c2c8219e3a4e35586b8cb2712cb92'
            )
          }}
        </div>
        <div
          v-if="langList.length > 1"
          class="submenu-trigger"
          @mouseenter="langMenuVisible = true"
          @mouseleave="langMenuVisible = false"
        >
          <div
            class="child-menu-item hover-bg flex items-center justify-between"
            data-testid="user-menu-language"
          >
            <span>{{
              $t(
                'views.layout.components.MenuList.261346637c008902458d82706326ef2c'
              )
            }}</span>
            <BotIcon name="left-arrow" :size="16" :padding="0" :rotate="180" />
          </div>
          <div
            v-show="langMenuVisible"
            class="lang-submenu"
            data-testid="user-menu-language-panel"
          >
            <div class="lang-submenu-panel">
              <div
                v-if="IS_DOMESTIC"
                class="child-menu-item hover-bg flex items-center justify-between"
                data-testid="user-menu-language-zh_CN"
                @click="handleChangeLang('zh_CN')"
              >
                <div class="flex items-center gap-[6px] cursor-pointer">
                  <img
                    src="@/assetsCommon/layout/chinese.png"
                    alt="chinese"
                    width="16"
                    height="16"
                  />
                  <span>{{
                    $t(
                      'views.layout.components.Avatar.7f8132d0848c7b6455dd1b28fbe84839'
                    )
                  }}</span>
                </div>
                <img
                  v-if="lang === 'zh_CN'"
                  src="@/assetsCommon/layout/select.png"
                  alt="right-arrow"
                  width="20"
                  height="20"
                />
              </div>
              <div
                class="child-menu-item hover-bg flex items-center justify-between"
                :data-testid="`user-menu-language-${englishLang}`"
                @click="handleChangeLang(englishLang)"
              >
                <div class="flex items-center gap-[6px] cursor-pointer">
                  <img
                    src="@/assetsCommon/layout/english.png"
                    alt="english"
                    width="16"
                    height="16"
                  />
                  <span>English</span>
                </div>
                <img
                  v-if="lang === englishLang"
                  src="@/assetsCommon/layout/select.png"
                  alt="right-arrow"
                  width="20"
                  height="20"
                />
              </div>
              <div
                v-if="!IS_DOMESTIC"
                class="child-menu-item hover-bg flex items-center justify-between"
                data-testid="user-menu-language-ja_JP"
                @click="handleChangeLang('ja_JP')"
              >
                <div class="flex items-center gap-[6px] cursor-pointer">
                  <img
                    src="@/assetsCommon/layout/japanese.svg"
                    alt="japanese"
                    width="16"
                    height="16"
                  />
                  <span>{{
                    $t(
                      'views.layout.components.UserMenu.a2f86bd7062c275cc373b1bdc973e8a7'
                    )
                  }}</span>
                </div>
                <img
                  v-if="lang === 'ja_JP'"
                  src="@/assetsCommon/layout/select.png"
                  alt="right-arrow"
                  width="20"
                  height="20"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          class="child-menu-item hover-bg"
          data-testid="open-feedback-button"
          @click="handleFeedback"
        >
          {{
            $t(
              'views.layout.components.MenuList.d3cd9015acb12b4313a6798829474515'
            )
          }}
        </div>
        <div class="border-t mt-[4px] mb-[4px] mx-[12px]" />
        <div
          class="child-menu-item hover-bg login-out"
          data-testid="logout-button"
          @click="handleLoginOut"
        >
          {{
            $t(
              'views.layout.components.MenuList.ed42ebefbd373068a4389ca2bf467696'
            )
          }}
        </div>
      </div>
    </template>
    <slot />
  </u-popover>
  <AccountSetting ref="accountSetting" />
  <Feedback ref="feedback" />
  <BindEmail ref="bindEmail" />
</template>
<script>
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { message } from '@koi-design/uxd-ui';
import dayjs from 'dayjs';
import { MANAGE_URL } from '@/api/urls';
import { getLoginCodeApi, loginOutApi } from '@/api/auge';
import { logoutApi } from '@/api/permission';
import { getPointsSummaryApi } from '@/api/billing';
import OneLineText from '@/components/OneLineText.vue';
import AccountSetting from '@V/layout/Dialog/AccountSetting.vue';
import Feedback from '@V/layout/Dialog/Feedback.vue';
import BindEmail from '@V/layout/Dialog/BindEmail.vue';
import { notifyAccountStatusChange } from '@/libs/accountSync';
import { langList } from '@/locales/i18n';

export default defineComponent({
  name: 'UserMenu',
  components: {
    AccountSetting,
    Feedback,
    BindEmail,
    OneLineText,
  },
  props: {
    // Popover placement: expanded sidebar uses top-end (default),
    // the collapsed sidebar passes top-start so the menu aligns to the left.
    placement: {
      type: String,
      default: 'top-end',
    },
    // [skidding, distance]: expanded sidebar shifts right to align with the
    // header avatar, the collapsed sidebar uses no skidding.
    offset: {
      type: Array,
      default: () => [56, 8],
    },
    // Disables the hover trigger, e.g. while another popover (ChangeSpace)
    // is open so the two popovers never overlap.
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    // Close the popover when it gets disabled (the space menu opens) so an
    // already-hovered user menu does not stay on screen.
    watch(
      () => props.disabled,
      (val) => {
        if (val) popoverVisible.value = false;
      }
    );
    const popoverVisible = ref(false);
    // top-start needs a separate dropdown class so the arrow can be pinned
    // near the menu's left edge instead of the right one.
    const dropdownClass = computed(() =>
      props.placement === 'top-start'
        ? 'reset-dropdown reset-dropdown-start'
        : 'reset-dropdown'
    );
    const store = useStore();
    const router = useRouter();
    const avatar = computed(() => store.getters.botUserInfo?.avatar);
    const isCompanyUser = computed(() => store.getters.isCompanyUser);
    const accountSetting = ref(null);
    const homelandInfo = computed(() => store.getters.homelandInfo);
    const canViewCreditQuota = computed(() => {
      return isCompanyUser.value || homelandInfo.value?.role === 'home_owner';
    });
    const creditBalance = ref(null);
    onMounted(async () => {
      if (!canViewCreditQuota.value) return;
      try {
        const response = await getPointsSummaryApi();
        const data = response?.data?.data || response?.data || response || {};
        creditBalance.value = data.remaining_points;
      } catch (error) {
        creditBalance.value = null;
      }
    });
    const formatNumber = (value) => {
      if (value === null || value === undefined || value === '') return '--';
      const parts = String(value).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    };
    const handleCreditQuota = () => {
      popoverVisible.value = false;
      router.push('/creditQuota');
    };
    const userExpireTime = computed(() => {
      return store.getters.userExpireTime;
    });
    const shouldShowExpireTime = computed(() => {
      if (!userExpireTime.value) return false;

      try {
        const expireDate = dayjs(userExpireTime.value);
        const currentDate = dayjs();
        const yearsDifference = expireDate.diff(currentDate, 'year', true);

        return yearsDifference < 10;
      } catch (e) {
        return false;
      }
    });
    const nickName = computed(() => store.getters.botUserInfo?.nickname);
    const userName = store.getters.botUserInfo?.userName;

    const closeAllDialog = () => {
      // feedback.value.show(false);
      // cancelAccount.value.show(false);
      // changeLang.value.show(false);
      // bindEmail.value.show(false);
      // modifyPassword.value = false;
    };
    const handleAccount = () => {
      closeAllDialog();
      popoverVisible.value = false;
      accountSetting.value.show();
    };
    const storedLang = localStorage.getItem('language');
    // Fall back to the first entry of the active build's list, so the checked
    // item always matches what i18n actually loaded.
    const lang = ref(
      langList.some((item) => item.value === storedLang)
        ? storedLang
        : langList[0].value
    );
    // Gaia build loads the en_gaia bundle for English (en_US domestically).
    const englishLang = langList.find((item) =>
      item.value.startsWith('en')
    ).value;
    // The language submenu lives inside the outer popover DOM, so hovering it
    // does not trigger the outer popover's mouseleave and close the menu.
    const langMenuVisible = ref(false);
    watch(popoverVisible, (val) => {
      if (!val) langMenuVisible.value = false;
    });
    const handleChangeLang = (val) => {
      if (val === lang.value) return;
      localStorage.setItem('language', val);
      window.location.reload();
    };

    const feedback = ref(null);
    const handleFeedback = () => {
      closeAllDialog();
      popoverVisible.value = false;
      feedback.value.show();
    };
    const bindEmail = ref(null);
    const handleEmail = () => {
      bindEmail.value?.show();
    };
    const handleLoginOut = async () => {
      try {
        if (isCompanyUser.value) {
          await loginOutApi();
        } else {
          await logoutApi({
            refresh_token: store.getters.botUserInfo?.refresh_token,
          });
        }
      } catch (error) {
        console.warn('Logout API error:', error);
      } finally {
        notifyAccountStatusChange('logout');
        localStorage.removeItem('sbtToken');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('auth_provider');
        localStorage.removeItem('azure_token_created_time');
        window.location.href = `${window.location.origin}/login`;
      }
    };

    const handleManage = async () => {
      try {
        const res = await getLoginCodeApi({
          loginType: 1,
        });
        window.open(`${MANAGE_URL(res.code)}`, '_blank');
      } catch (error) {
        message.error(error);
      }
    };

    return {
      popoverVisible,
      dropdownClass,
      avatar,
      userExpireTime,
      shouldShowExpireTime,
      nickName,
      userName,
      homelandInfo,
      isCompanyUser,
      canViewCreditQuota,
      creditBalance,
      formatNumber,
      handleCreditQuota,
      handleAccount,
      accountSetting,
      lang,
      langList,
      englishLang,
      langMenuVisible,
      handleChangeLang,
      handleFeedback,
      feedback,
      handleEmail,
      handleLoginOut,
      handleManage,
    };
  },
});
</script>
<style scoped lang="less">
.child-menu {
  width: 260px;
  padding-top: 16px;
  border-radius: 8px 8px 8px 8px;
  display: flex;
  flex-direction: column;
  color: var(--color-text-icon-body-secondary);
  .avatar-setting {
    display: flex;
    height: 48px;
    padding: 0 16px;
    margin-bottom: 16px;
    .avatar {
      margin: 0;
    }
    .user {
      display: flex;
      flex-direction: column;
      margin-left: 13px;
      flex: 1;
      .nick-name {
        height: 20px;
        margin-bottom: 2px;
        width: 204px;
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--color-text-icon-body-primary);
      }
      .user-name {
        color: var(--color-text-icon-body-tertiary);
      }
    }
  }
}
.child-menu-item {
  height: 40px;
  width: 100%;
  line-height: 40px;
  padding: 0 18px;
  border-radius: 6px;

  &.acount {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
.credit-quota-entry {
  height: 42px;
  margin: 8px 4px;
  padding: 0 14px 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  border: 1px solid var(--border-primary-color-primary-border);
  color: var(--text-primary-color-primary-text);
  background: var(--background-module-bg-color-purple-light);
  cursor: pointer;

  &:hover {
    background: var(--background-primary-bg-color-primary);
  }
}
.credit-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.credit-title {
  color: var(--color-text-icon-body-primary);
  font-size: 12px;
}
.credit-value {
  font-size: 14px;
  font-weight: 600;
}
.active {
  background: var(--color-filled-background-filled-hover);
}
.submenu-trigger {
  position: relative;
  width: 100%;
}
.lang-submenu {
  position: absolute;
  top: 0;
  left: 100%;
  // Transparent padding bridges the gap between the trigger row and the panel,
  // so moving the cursor into the panel never leaves the popover hover area.
  padding-left: 10px;
  cursor: default;
  .lang-submenu-panel {
    min-width: 160px;
    padding: 6px;
    background: var(--color-filled-background-container-primary);
    border: 1px solid var(--color-border-divider);
    border-radius: var(--popover-border-radius);
    box-shadow: var(--popover-shadow);
  }
}
</style>
<style lang="less">
.reset-dropdown .uui-popover-inner {
  border: none !important;
  // Allow the language submenu to extend outside the popover box
  // (the global reset clips every popover with `overflow: hidden`).
  overflow: visible;
}
// Restore the popover arrow, which is hidden globally by the reset styles
// (.uui-popover-arrow { display: none } in reset.less and
// --popover-arrow-wrap-*: 0 in resetUxd.less).
.reset-dropdown {
  --popover-arrow-width: 8px;
  --popover-arrow-wrap-width: calc(var(--popover-arrow-width) + 4px);
  --popover-distance: var(--popover-arrow-wrap-width);

  .uui-popover-arrow {
    display: block;
    // Redundant on purpose: the arrow element consumes these variables itself,
    // so they win over the global zeroed values regardless of cascade order.
    --popover-arrow-width: 8px;
    --popover-arrow-wrap-width: calc(var(--popover-arrow-width) + 4px);
    // Pin the arrow horizontally (uxd-ui leaves it at the static position
    // for top-end placement). Decrease to move left, increase to move right.
    left: 192px;
  }
}
// Collapsed sidebar (top-start): keep the arrow near the menu's left edge.
.reset-dropdown-start .uui-popover-arrow {
  left: 16px;
}
</style>
